import { useEffect, useState } from 'react';
import { Button, Grid } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';

import { useLoadData } from '../../hooks/useLoadData';
import { ApiResponse } from '../../models/api-response';
import { usersApiKeyStore } from '../../api/users-api-key-store';
import { officeApiKeyStore } from '../../api/office-api-key-store';
import { projectsApiKeyStore } from '../../api/projects-api-key-store';
import { customersApiKeyStore } from '../../api/customers-api-key-store';
import { workOrdersApiKeyStore } from '../../api/workorders-api-key-store';
import { divisionApiKeyStore } from '../../api/masters/division-api-key-store';
import { marketAreaApiKeyStore } from '../../api/masters/market-area-api-key-store';
import FormDateField, { DateFormat } from '../../components/hook-form/form-date.field.component';
import { FormAutoCompleteSelectDynamic } from '../../components/hook-form/form-autocomplete-select.dynamic.component';
import { FormAutoCompleteMultiSelectDynamic } from '../../components/hook-form/form-autocomplete-multi-select.dynamic.component';
import { useAuthorize } from '../../hooks/use-authorize';
import { UserPermissionName } from '../../models/user-permission-name';
import { departmentsApiKeyStore } from '../../api/masters/department-api-key-store';

interface FiltersComponent {
  setStatusId: (statusId: any) => void;
}
export const FiltersComponent = ({ setStatusId }: FiltersComponent) => {
  const { setValue } = useFormContext();
  const [inspectorDisabled, setInspectorDisabled] = useState<boolean>(false);
  const [officeDisabled, setOfficeDisabled] = useState<boolean>(true);
  const [projectDisabled, setProjectDisabled] = useState<boolean>(true);
  const [disabledToDate, setDisabledToDate] = useState<boolean>(false);

  const { data, isLoading } = useLoadData<ApiResponse<any>>(usersApiKeyStore.getUserDetails());

  const { data: statusData, isLoading: isStatusLoading } = useLoadData<ApiResponse<any>>(
    workOrdersApiKeyStore.getWorkOrderStatus('InspectionCalendar')
  );

  const isViewAllCalender = useAuthorize([UserPermissionName.VIEW_ALL_CALENDAR]);
  const isViewOwnCalender = useAuthorize([UserPermissionName.VIEW_OWN_CALENDAR]);
  const isViewTeamCalender = useAuthorize([UserPermissionName.VIEW_TEAM_CALENDAR]);

  useEffect(() => {
    if (data) {
      const userRole: string = data?.data?.roles;

      if (isViewOwnCalender && !isViewAllCalender && !isViewTeamCalender) {
        setInspectorDisabled(true);
        setValue('userIDs', [{ userID: data?.data?.userID, fullName: data?.data?.fullName }]);
      }
    }
  }, [data]);

  useEffect(() => {
    if (statusData) {
      const scheduleStatus = statusData?.data?.find((element: any) => element.workOrderStatus == 'Scheduled');
      setStatusId(scheduleStatus?.workOrderStatusID);
      setValue(
        'workOrderStatusID',
        scheduleStatus
          ? [{ workOrderStatusID: Number(scheduleStatus?.workOrderStatusID), workOrderStatus: 'Scheduled' }]
          : []
      );
    }
  }, [statusData]);

  const handleCustomerChange = (value: any) => {
    setOfficeDisabled(!value);
    setProjectDisabled(true);
    setValue('officeIDs', null);
    setValue('projectIDs', null);
  };

  const handleOfficeChange = (value: any) => {
    setProjectDisabled(!(value.length > 0));
    setValue('projectIDs', null);
  };

  const handleFromDate = (date: any) => {
    setDisabledToDate(!!date);
    setValue('toDate', '');
  };

  return (
    <Grid container spacing={1}>
      <Grid item lg={12} sm={12} xl={12} xs={12}>
        <Grid container spacing={1}>
          <Grid item xl={3} lg={3} sm={4} xs={6}>
            <FormAutoCompleteSelectDynamic
              name="customer"
              label="Customer"
              onChange={handleCustomerChange}
              getQueryKeyConfig={() => customersApiKeyStore.getCustomers()}
              optionsValueKey="customerID"
              optionsLabelKey="customerName"
            />
          </Grid>
          <Grid item xl={3} lg={3} sm={4} xs={6}>
            <FormAutoCompleteMultiSelectDynamic
              name="officeIDs"
              label="Offices"
              watchKeys={['customer']}
              onChange={handleOfficeChange}
              getQueryKeyConfig={(inputData: any) =>
                officeApiKeyStore.getCustomerOffices(inputData.customer?.customerID)
              }
              optionsLabelKey="officeName"
              optionsValueKey="officeID"
              disabled={officeDisabled}
            />
          </Grid>
          <Grid item xl={3} lg={3} sm={4} xs={6}>
            <FormAutoCompleteMultiSelectDynamic
              name="projectIDs"
              label="Projects"
              watchKeys={['officeIDs']}
              getQueryKeyConfig={(inputData: any) =>
                projectsApiKeyStore.getOfficesProjects(inputData.officeIDs?.map((o: any) => o.officeID))
              }
              optionsLabelKey="projectName"
              optionsValueKey="projectID"
              disabled={projectDisabled}
            />
          </Grid>
          <Grid item xl={3} lg={3} sm={4} xs={6}>
            <FormAutoCompleteMultiSelectDynamic
              name="marketAreaIDs"
              label="Market Areas"
              getQueryKeyConfig={() => marketAreaApiKeyStore.getMarketAreas()}
              optionsLabelKey="marketAreaName"
              optionsValueKey="marketAreaID"
            />
          </Grid>
          <Grid item xl={3} lg={3} sm={4} xs={6}>
            <FormAutoCompleteMultiSelectDynamic
              name="userIDs"
              label="Inspectors"
              disabled={inspectorDisabled}
              getQueryKeyConfig={
                isViewTeamCalender && !isViewAllCalender
                  ? () => usersApiKeyStore.getInspectorTeamforManager()
                  : () => usersApiKeyStore.getUsersByRole('ins')
              }
              optionsLabelKey="fullName"
              optionsValueKey="userID"
            />
          </Grid>
          <Grid item xl={3} lg={3} sm={4} xs={6}>
            <FormAutoCompleteMultiSelectDynamic
              name="workOrderStatusID"
              label="Status"
              getQueryKeyConfig={() => workOrdersApiKeyStore.getWorkOrderStatus('InspectionCalendar')}
              optionsLabelKey="workOrderStatus"
              optionsValueKey="workOrderStatusID"
            />
          </Grid>
          <Grid item lg={2} sm={4} xl={2} xs={6}>
            <FormDateField
              label={'Date From'}
              name="fromDate"
              onChange={handleFromDate}
              format={DateFormat.SIMPLE_DATE}
            />
          </Grid>
          <Grid item lg={2} sm={4} xl={2} xs={6}>
            <FormDateField
              label={'Date To'}
              name="toDate"
              disabled={!disabledToDate}
              format={DateFormat.SIMPLE_DATE}
              dateLimits={{ minDateEnabled: true, minDateValue: 'fromDate' }}
            />
          </Grid>
          <Grid item lg={3} sm={6} xl={3} xs={6}>
            <FormAutoCompleteMultiSelectDynamic
              name="departments"
              label="Departments"
              getQueryKeyConfig={() => departmentsApiKeyStore.getDepartmentsWithManager()}
              optionsLabelKey="departmentName"
              optionsValueKey="departmentID"
            />
          </Grid>
          <Grid item lg={3} sm={6} xl={3} xs={6}>
            <FormAutoCompleteMultiSelectDynamic
              name="divisions"
              label="Divisions"
              getQueryKeyConfig={() => divisionApiKeyStore.getDivisions()}
              optionsLabelKey="divisionName"
              optionsValueKey="divisionID"
            />
          </Grid>
          <Grid item xl={4} lg={4} sm={4} xs={6}>
            <Button
              type="submit"
              title="Filter"
              disabled={isStatusLoading || isLoading}
              variant="contained"
              startIcon={<FilterAltOutlinedIcon />}
            >
              Filter
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
